import { colors, fonts, Spacer } from 'app-v2/design-systems'
import { SIZE, BORDER, SPACING } from 'app-v2/design-tokens'
import React, { useEffect, useRef, useState } from 'react'
import { H2, H5, Text } from '../../common/typography'
import { WinningIcon } from '../duel/components/Winning/WinningIcon'
import { useIntl } from 'react-intl'
import {
    Chevron,
    CircleDownloadIcon,
    CircleQuestionIcon,
    CircleShareIcon
} from '../../assets/icons'
import { getQrCode } from '../../common/utils'
import type { WinningType } from '../types'
import { Redirect, useHistory, useParams } from 'react-router-dom'
import { InfoKeepCard } from '../components/InfoKeepCard'
import { Button } from 'app-v2/design-systems/components/inputs/button'
import { useTPKState } from 'shared/store/tu-preferes-king'
import styled from 'styled-components'
import { media, breakpoints } from '../../assets/styles'

import { TPKColors } from '../colors'
import { useWindowDimensions } from '../utils'
import { TPKLayout } from '../components/TPKLayout'
import { useReactToPrint, type HTMLIframeElement } from 'react-to-print'
import html2pdf from 'html2pdf.js'
import { useDispatch, useSelector } from 'react-redux'
import { fetchTPKGoodiesData } from 'shared/store/tu-preferes-king'
import type { TPKState } from 'shared/store/tu-preferes-king'
import type { RootState } from 'shared/store/reducers'

export function TuPreferesKingQrCode() {
    const intl = useIntl()
    const history = useHistory()
    const [qrCode, setQrCode] = useState('')
    const { width: windowWidth } = useWindowDimensions()
    const isSmall = windowWidth <= breakpoints.small
    const { tpkToken } = useParams()
    const data = useTPKState(tpkToken, { autoload: true })
    // @ts-ignore
    const downloadComponentRef = useRef()
    const [isLoadingPDF, setIsLoadingPDF] = useState(false)
    const dispatch = useDispatch()
    const state: TPKState = useSelector((s: RootState) => s.tuPreferesKingState)

    useEffect(() => {
        dispatch(fetchTPKGoodiesData())
    }, [dispatch])

    useEffect(() => {
        if (data.duel && data.duel.couponCode) {
            setQrCodeState(data.duel.couponCode)
        }
    }, [data])

    if (
        !data.duel ||
        (data.duel && (!data.duel.email || !data.duel.couponCode))
    ) {
        return (
            <Redirect
                to={intl.formatMessage(
                    { id: 'routes.tupreferesking.duel.tpkToken' },
                    {
                        tpkToken
                    }
                )}
            />
        )
    }
    const duel = data.duel
    const type: WinningType = duel.childGain.selected ? 'CHILD' : 'PARENT'
    const winning = duel.childGain.selected ? duel.childGain : duel.parentGain
    const showGoodiesButton =
        //$FlowFixMe
        state.goodies?.findIndex(
            goodies =>
                //$FlowFixMe
                goodies.name.split(' ')?.[0] === winning.name.split(' ')?.[0]
        ) !== -1

    const setQrCodeState = async (code: string) => {
        const qrCode = await getQrCode(code, colors.bbqBrown)
        setQrCode(qrCode)
    }

    const formatDate = (validityExpirationDate: string) => {
        const date = validityExpirationDate.split('T')
        const dateToPost = date[0].split('-')
        return `${dateToPost[2]}/${dateToPost[1]}/${dateToPost[0]}`
    }

    const handleDownloadButtonClick = useReactToPrint({
        onBeforePrint: () => {
            setIsLoadingPDF(true)
        },
        content: () => downloadComponentRef.current.cloneNode(true),
        print: async (printIframe: HTMLIframeElement) => {
            try {
                // Do whatever you want here, including asynchronous work
                const document = printIframe.contentDocument

                if (document) {
                    const elements = document.getElementsByClassName(
                        'not-in-pdf'
                    )
                    while (elements.length > 0) {
                        elements[0].parentNode.removeChild(elements[0])
                    }

                    document.getElementsByTagName('html')[0].style.height =
                        '1120px'
                    document.getElementById('main-card').style.marginTop =
                        '-200px'
                    document.getElementById('main-title').style.marginTop =
                        '10px'

                    const html = document.getElementsByTagName('html')[0]

                    await html2pdf()
                        .from(html)
                        .save(
                            intl.formatMessage({
                                id: 'tupreferesking.qrcode.pdf.name'
                            })
                        )
                }
            } catch (e) {
            } finally {
                setIsLoadingPDF(false)
            }
        }
    })

    const shareByMail = () => {
        window.location = `mailto:?subject=${intl.formatMessage({
            id: 'tupreferesking.qrcode.mail.subject'
        })}&body=${intl.formatMessage({
            id: 'tupreferesking.qrcode.mail.body.title'
        })}%0A${intl.formatMessage({
            id: 'tupreferesking.qrcode.mail.body'
        })}%0A${process.env.REACT_APP_FRONT_BASE_URL || ''}${intl.formatMessage(
            { id: 'routes.tupreferesking.qrCode.tpkToken' },
            {
                tpkToken
            }
        )}`
    }

    return (
        <TPKLayout
            ref={downloadComponentRef}
            header={{
                mediumBackground: true,
                withSurf: true,
                withBuoy: true
            }}>
            <StyledCard id="main-card">
                <StyledWinningIconContainer className="not-in-pdf">
                    <WinningIcon winning={winning} type={type} selected />
                </StyledWinningIconContainer>
                {!isSmall && <StyledShadow className="not-in-pdf" />}
                <Spacer className="not-in-pdf" size={SIZE.XXL} />
                <StyledTitleContainer id="main-title">
                    <H2 center>{winning.name}</H2>
                </StyledTitleContainer>

                {duel.validityExpirationDate && (
                    <StyledDateContainer>
                        <Text>
                            {intl.formatMessage(
                                {
                                    id: 'tupreferesking.qrcode.expirationDate'
                                },
                                {
                                    date: formatDate(
                                        duel.validityExpirationDate
                                    )
                                }
                            )}
                        </Text>
                    </StyledDateContainer>
                )}
                <StyledQrCodeContainer>
                    <Image src={qrCode} alt="QrCode" front />
                    <H2>{duel.couponCode}</H2>
                </StyledQrCodeContainer>

                <StyledButtonContainer className="not-in-pdf">
                    <StyledButton onClick={() => shareByMail()}>
                        <CircleShareIcon color={colors.bbqBrown} size={62} />
                        <Text>
                            {intl.formatMessage({
                                id: 'tupreferesking.qrcode.share'
                            })}
                        </Text>
                    </StyledButton>
                    <StyledButton
                        onClick={() => handleDownloadButtonClick()}
                        disabled={isLoadingPDF}>
                        <CircleDownloadIcon
                            color={
                                isLoadingPDF
                                    ? colors.greyDisable
                                    : colors.bbqBrown
                            }
                            size={62}
                        />
                        <Text
                            color={
                                isLoadingPDF
                                    ? colors.greyDisable
                                    : colors.bbqBrown
                            }>
                            {intl.formatMessage({
                                id: 'tupreferesking.qrcode.download'
                            })}
                        </Text>
                    </StyledButton>
                </StyledButtonContainer>

                {showGoodiesButton && winning.immediateGain && (
                    <Button
                        buttonColor={colors.bbqBrown}
                        labelColor={colors.white}
                        onClick={() => {
                            history.push(
                                intl.formatMessage({
                                    id: 'routes.tupreferesking.goodiesGain'
                                })
                            )
                        }}
                        className="not-in-pdf">
                        {intl.formatMessage({
                            id: 'tupreferesking.qrcode.disponibility'
                        })}
                    </Button>
                )}
                <StyledExplanationContainer>
                    <StyledTextContainer>
                        <CircleQuestionIcon size={25} />
                        <H5>
                            {intl.formatMessage({
                                id: 'tupreferesking.qrcode.explanations.title'
                            })}
                        </H5>
                    </StyledTextContainer>
                    <Text center={!isSmall}>
                        {intl.formatMessage({
                            id: 'tupreferesking.qrcode.explanations.description'
                        })}
                    </Text>
                </StyledExplanationContainer>

                <StyledMandatoryItem>
                    <Text font={fonts.flameRegular}>
                        {intl.formatMessage({
                            id: 'tupreferesking.qrcode.mandatory'
                        })}
                    </Text>

                    <Text>{winning.legalMentions}</Text>
                </StyledMandatoryItem>
            </StyledCard>

            <div className="not-in-pdf">
                <Spacer size={SIZE.XXL} />

                <InfoKeepCard />
            </div>
        </TPKLayout>
    )
}

const StyledShadow = styled.div`
    position: absolute;
    top: -300px;
    width: 550px;
    height: 550px;
    background: radial-gradient(${colors.white}, transparent, transparent);
    z-index: 1;
`

const StyledCard = styled.div`
    box-sizing: border-box;
    z-index: 10;
    position: relative;
    padding: ${SPACING.L};
    max-width: 900px;
    width: 100%;
    background: ${colors.white};
    border-radius: ${BORDER.RADIUS.L};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${SPACING.S};
    ${media.small`gap: ${SPACING.S};`}
    ${media.small`
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);`}
    ${media.small`margin-top: -50px;`}
`

const StyledWinningIconContainer = styled.div`
    display: flex;
    justify-content: center;
    position: absolute;
    top: -150px;
    width: 190px;
    padding: ${SPACING.XXXL};
    ${media.small`top: -200px;`}
    ${media.small`width: 100%;`}
`

const StyledTitleContainer = styled.div`
    margin-top: ${SPACING.L};
    ${media.small`margin-top: 74px;`}
    z-index: 2;
`

const StyledDateContainer = styled.div`
    background-color: ${colors.mayoEggWhite};
    border-radius: ${BORDER.RADIUS.XXXL};
    padding: ${SPACING.XXXS} ${SPACING.XS};
    z-index: 3;
`

const StyledTextContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${SPACING.XS};
    align-items: center;
`

const StyledQrCodeContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${SPACING.XS};
    padding: ${SPACING.S};
    border: ${BORDER.WIDTH.XS} solid ${TPKColors.circleColor};
    border-radius: ${BORDER.RADIUS.L};
    margin: ${SPACING.S};
    z-index: 2;
`

const StyledButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    justify-content: center;
    gap: ${SPACING.XXXL};
`

const StyledButton = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${SPACING.XS};
    :active {
        opacity: 0.4;
    }
    :hover {
        cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    }
`

const StyledExplanationContainer = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${TPKColors.faqBorder};
    border-radius: ${BORDER.RADIUS.L};
    padding: ${SPACING.S};
    gap: ${SPACING.XXS};
    margin: ${SPACING.XS} 0;
    ${media.small`align-items: center;`}
`

const Image = styled.img`
    height: 250px;
    width: 250px;
    ${media.small`height: 350px;`};
    ${media.small`width: 350px;`};
    margin: -${SPACING.XS};
`

const StyledMandatoryItem = styled.div`
    display: flex;
    background: ${colors.white};
    flex-direction: column;
    width: 100%;
`
